<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  reactive,
  ref,
  toRaw,
  watch,
} from "vue";
import {
  AlertOctagon,
  Check,
  CheckSquare2,
  ChevronDown,
  ClipboardPaste,
  Download,
  EyeOff,
  FileText,
  Link2,
  MessageSquareText,
  Plus,
  Send,
  Trash2,
  Upload,
  UserRound,
  X,
} from "lucide-vue-next";
import RichTextRenderer from "@/components/content/RichTextRenderer.vue";
import PersonAvatar from "@/components/people/PersonAvatar.vue";
import ArtifactDeleteDialog from "@/components/ArtifactDeleteDialog.vue";
import TaskDeleteDialog from "@/components/task/TaskDeleteDialog.vue";
import ScheduleImpactDialog from "@/components/views/ScheduleImpactDialog.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import { ApiError } from "@/services/api";
import {
  calculateProjectFinish,
  fullDate,
  isProjectWorkingDay,
} from "@/utils/dates";
import { calculateTaskProgress, descendantTasks } from "@/utils/progress";
import {
  calculatedWorkItemProgress,
  questionHasAnswer,
} from "@/utils/work-items";
import type {
  Participant,
  ProjectTask,
  RichTextDocument,
  ScheduleImpact,
  TaskComment,
  TaskFile,
  TaskStatus,
  Visibility,
} from "@/types/models";
import {
  emptyRichTextDocument,
  replaceIneligibleMentions,
  richTextIsEmpty,
  richTextMentionIds,
  richTextOrPlain,
} from "@/utils/rich-text";
import { parseItemLines } from "@/utils/task-import";
import {
  taskDeletionBlockers,
  taskDeletionBlockersFromDetails,
  type TaskDeletionBlocker,
} from "@/utils/task-deletion";

const props = defineProps<{
  task: ProjectTask | null;
  open: boolean;
  initialSection?: string;
  initialStatus?: TaskStatus;
}>();
const RichTextEditor = defineAsyncComponent(
  () => import("@/components/content/RichTextEditor.vue"),
);
const emit = defineEmits<{
  close: [];
  saved: [];
  openTask: [id: string, section?: string];
}>();
const workspace = useWorkspaceStore();
const draft = reactive<Partial<ProjectTask>>({});
const base = ref<ProjectTask | null>(null);
const comment = ref<RichTextDocument>(emptyRichTextDocument());
const commentEditor = ref<InstanceType<typeof RichTextEditor> | null>(null);
const commentValid = ref(true);
const commentDraftId = ref(createCommentId());
const commentVisibility = ref<Visibility>("Shared");
const commentPosting = ref(false);
const commentError = ref("");
const commentAudienceMessage = ref("");
const taskFileInput = ref<HTMLInputElement | null>(null);
const taskFileUploading = ref(false);
const taskFileError = ref("");
const taskFileDownloadingId = ref("");
const fileToDelete = ref<TaskFile | null>(null);
const commentToDelete = ref<TaskComment | null>(null);
const artifactDeleting = ref(false);
const artifactDeleteError = ref("");
const descriptionValid = ref(true);
const notesValid = ref(true);
const activeSection = ref("overview");
const descriptionExpanded = ref(false);
const saving = ref(false);
const saveError = ref("");
const newChecklistLabel = ref("");
const newQuestionLabel = ref("");
const newChecklistInput = ref<HTMLInputElement | null>(null);
const newQuestionInput = ref<HTMLInputElement | null>(null);
const newQuestionType =
  ref<ProjectTask["questions"][number]["type"]>("Short text");
const newQuestionOptions = ref("");
const checklistImportOpen = ref(false);
const checklistImportText = ref("");
const questionImportOpen = ref(false);
const questionImportText = ref("");
const checklistImportLines = computed(() =>
  parseItemLines(checklistImportText.value),
);
const questionImportLines = computed(() =>
  parseItemLines(questionImportText.value),
);
const checklistCapacity = computed(() => 100 - (draft.checklist?.length || 0));
const questionCapacity = computed(() => 100 - (draft.questions?.length || 0));
const checklistImportError = computed(() =>
  checklistImportLines.value.length > checklistCapacity.value
    ? `You can add ${Math.max(0, checklistCapacity.value)} more checklist ${checklistCapacity.value === 1 ? "item" : "items"}. Remove extra lines before adding.`
    : "",
);
const questionImportError = computed(() =>
  questionImportLines.value.length > questionCapacity.value
    ? `You can add ${Math.max(0, questionCapacity.value)} more ${questionCapacity.value === 1 ? "question" : "questions"}. Remove extra lines before adding.`
    : "",
);
const dependencyOpen = ref(false);
const dependencyQuery = ref("");
const dependencyError = ref("");
const completionAttempted = ref(false);
const scheduleImpact = ref<ScheduleImpact | null>(null);
const saveMenuOpen = ref(false);
const saveSplit = ref<HTMLElement | null>(null);
const saveMenu = ref<HTMLElement | null>(null);
const saveMenuButton = ref<HTMLButtonElement | null>(null);
const taskDeleteOpen = ref(false);
const taskDeleting = ref(false);
const taskDeleteError = ref("");
const taskDeleteServerBlockers = ref<TaskDeletionBlocker[]>([]);
const blockerInput = ref<HTMLTextAreaElement | null>(null);
const reopenReasonInput = ref<HTMLTextAreaElement | null>(null);
type PendingDeletion = {
  kind: "checklist" | "question";
  id: string;
  label: string;
};
const pendingDeletion = ref<PendingDeletion | null>(null);
const deleteDialog = ref<HTMLElement | null>(null);
const keepDeleteButton = ref<HTMLButtonElement | null>(null);
let deleteOpener: HTMLElement | null = null;
const statusOptions: TaskStatus[] = [
  "Not started",
  "In progress",
  "Blocked",
  "In review",
  "Complete",
  "Not applicable",
  "Cancelled",
];
type EditableField =
  | "type"
  | "title"
  | "description"
  | "descriptionRichText"
  | "notes"
  | "notesRichText"
  | "status"
  | "ownerId"
  | "visibility"
  | "blocker"
  | "issueOwnerId"
  | "issueOwnerNeeded"
  | "startDate"
  | "dueDate"
  | "duration"
  | "progress"
  | "sectionId"
  | "parentId"
  | "checklist"
  | "questions";
interface DraftConflict {
  field: EditableField;
  label: string;
  mine: unknown;
  latest: unknown;
}
const editableFields: EditableField[] = [
  "type",
  "title",
  "description",
  "descriptionRichText",
  "notes",
  "notesRichText",
  "status",
  "ownerId",
  "visibility",
  "blocker",
  "issueOwnerId",
  "issueOwnerNeeded",
  "startDate",
  "dueDate",
  "duration",
  "progress",
  "sectionId",
  "parentId",
  "checklist",
  "questions",
];
const fieldLabels: Record<EditableField, string> = {
  type: "Work-item type",
  title: "Task name",
  description: "Description",
  descriptionRichText: "Description formatting",
  notes: "Notes",
  notesRichText: "Notes formatting",
  status: "Status",
  ownerId: "Owner",
  visibility: "Visibility",
  blocker: "Blocker",
  issueOwnerId: "Issue owner",
  issueOwnerNeeded: "Issue owner needed",
  startDate: "Start date",
  dueDate: "Due date",
  duration: "Duration",
  progress: "Progress",
  sectionId: "Section",
  parentId: "Parent task",
  checklist: "Checklist",
  questions: "Questions",
};
const conflicts = ref<DraftConflict[]>([]);
const panel = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
let returnFocus: HTMLElement | null = null;
let priorOverflow = "";

function createCommentId() {
  return `cmt_${crypto.randomUUID().replaceAll("-", "").slice(0, 16)}`;
}

function normalizedSection(section?: string) {
  return section === "schedule" ? "overview" : section || "overview";
}

const clone = <T,>(value: T): T =>
  value === undefined ? value : (JSON.parse(JSON.stringify(toRaw(value))) as T);
const same = (left: unknown, right: unknown) =>
  JSON.stringify(left) === JSON.stringify(right);
function setDraftField(field: EditableField, value: unknown) {
  (draft as Record<EditableField, unknown>)[field] = clone(value);
}

function replaceDraft(task: ProjectTask) {
  Object.keys(draft).forEach((key) => delete draft[key as keyof ProjectTask]);
  const normalized = clone(task);
  normalized.descriptionRichText = richTextOrPlain(
    task.descriptionRichText,
    task.description,
  );
  if (task.type === "task")
    normalized.notesRichText = richTextOrPlain(task.notesRichText, task.notes);
  normalized.parentId ||= "";
  Object.assign(draft, normalized);
  descriptionExpanded.value = !richTextIsEmpty(normalized.descriptionRichText);
  base.value = clone(normalized);
  conflicts.value = [];
  saveError.value = "";
  completionAttempted.value = false;
  scheduleImpact.value = null;
  saveMenuOpen.value = false;
  taskDeleteOpen.value = false;
  taskDeleteError.value = "";
  taskDeleteServerBlockers.value = [];
  pendingDeletion.value = null;
  checklistImportOpen.value = false;
  checklistImportText.value = "";
  questionImportOpen.value = false;
  questionImportText.value = "";
}

function actorCanChangeStatus(task: ProjectTask) {
  return task.type !== "summary" && workspace.canEditPlan;
}

function applyInitialStatus() {
  if (
    props.task &&
    actorCanChangeStatus(props.task) &&
    props.initialStatus &&
    props.initialStatus !== draft.status
  )
    draft.status = props.initialStatus;
}

const canChangeStatus = computed(() =>
  Boolean(props.task && actorCanChangeStatus(props.task)),
);
const canDeleteTask = computed(
  () => Boolean(props.task) && workspace.canManageProject,
);
const localTaskDeleteBlockers = computed(() =>
  props.task
    ? taskDeletionBlockers(workspace.active?.tasks || [], [props.task.id])
    : [],
);
const displayedTaskDeleteBlockers = computed(() =>
  taskDeleteServerBlockers.value.length
    ? taskDeleteServerBlockers.value
    : localTaskDeleteBlockers.value,
);

const descriptionDocument = computed<RichTextDocument>({
  get: () =>
    draft.descriptionRichText ||
    richTextOrPlain(undefined, String(draft.description || "")),
  set: (value) => {
    draft.descriptionRichText = value;
  },
});
const notesDocument = computed<RichTextDocument>({
  get: () =>
    draft.notesRichText ||
    richTextOrPlain(undefined, String(draft.notes || "")),
  set: (value) => {
    draft.notesRichText = value;
  },
});

watch(
  () => props.task?.id,
  async () => {
    if (!props.task) return;
    replaceDraft(props.task);
    applyInitialStatus();
    commentVisibility.value = workspace.isInternal
      ? props.task.visibility
      : "Shared";
    activeSection.value = normalizedSection(props.initialSection);
    if (props.open && props.initialStatus && actorCanChangeStatus(props.task)) {
      await nextTick();
      await focusStatusDetails(props.initialStatus);
    }
  },
  { immediate: true },
);

watch(
  [() => workspace.canEditPlan, activeSection, () => props.task?.id],
  async () => {
    if (workspace.canEditPlan) return;
    await nextTick();
    panel.value
      ?.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >(
        ".task-panel__body input, .task-panel__body textarea, .task-panel__body select",
      )
      .forEach((control) => {
        control.disabled = true;
      });
    panel.value
      ?.querySelectorAll<HTMLElement>(
        '.task-panel__body [contenteditable="true"], .task-panel__body [contenteditable="plaintext-only"]',
      )
      .forEach((control) => control.setAttribute("contenteditable", "false"));
  },
  { flush: "post" },
);
watch(
  () => props.task?.version,
  () => {
    const source = props.task;
    const prior = base.value;
    if (!source || !prior || source.id !== prior.id) return;
    if (source.version === prior.version) return;
    const latest = clone(source);
    latest.descriptionRichText = richTextOrPlain(
      source.descriptionRichText,
      source.description,
    );
    if (source.type === "task")
      latest.notesRichText = richTextOrPlain(
        source.notesRichText,
        source.notes,
      );

    const nextConflicts: DraftConflict[] = [];
    for (const field of editableFields) {
      const localChanged = !same(draft[field], prior[field]);
      const remotelyChanged = !same(latest[field], prior[field]);
      if (!localChanged) setDraftField(field, latest[field]);
      else if (remotelyChanged && !same(draft[field], latest[field])) {
        nextConflicts.push({
          field,
          label: fieldLabels[field],
          mine: clone(draft[field]),
          latest: clone(latest[field]),
        });
      }
    }
    base.value = clone(latest);
    conflicts.value = nextConflicts;
    if (nextConflicts.length)
      saveError.value =
        "This task changed while you were editing. Your draft is still here. Choose a value for each overlapping change before saving.";
  },
);
watch(
  () => props.open,
  async (open) => {
    if (open) {
      if (props.task) {
        replaceDraft(props.task);
        applyInitialStatus();
      }
      activeSection.value = normalizedSection(props.initialSection);
      returnFocus = document.activeElement as HTMLElement;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      await nextTick();
      if (!(await focusStatusDetails(props.initialStatus)))
        closeButton.value?.focus();
    } else {
      taskDeleteOpen.value = false;
      document.body.style.overflow = priorOverflow;
      returnFocus?.focus();
    }
  },
);
watch(
  () => props.initialSection,
  (section) => {
    if (props.open && section) activeSection.value = normalizedSection(section);
  },
);
watch(
  () => props.initialStatus,
  async (status) => {
    if (
      !props.open ||
      !canChangeStatus.value ||
      !status ||
      status === draft.status
    )
      return;
    await selectSaveStatus(status);
  },
);
onBeforeUnmount(() => {
  document.body.style.overflow = priorOverflow;
});

const dirty = computed(() =>
  base.value
    ? editableFields.some((field) => !same(draft[field], base.value?.[field]))
    : false,
);
const sections = computed(() => [
  { id: "overview", label: "Overview" },
  ...(workspace.canEditPlan
    ? [{ id: "structure", label: "Structure" }]
    : hasDependencies.value
      ? [{ id: "structure", label: "Dependencies" }]
      : []),
  ...(props.task?.type === "task" ? [{ id: "notes", label: "Notes" }] : []),
  ...(props.task?.type === "checklist"
    ? [{ id: "responses", label: "Checklist" }]
    : []),
  ...(props.task?.type === "questionnaire"
    ? [{ id: "responses", label: "Questions" }]
    : []),
  { id: "files", label: "Files" },
  { id: "comments", label: `Comments ${props.task?.commentCount || ""}` },
]);
const mentionParticipants = computed(() =>
  (workspace.active?.participants || []).filter(canMention),
);
const taskMentionParticipants = computed(() =>
  (workspace.active?.participants || []).filter(
    (person) =>
      person.state !== "pending" &&
      (draft.visibility === "Shared" || person.kind === "internal"),
  ),
);
const taskFiles = computed(() =>
  (workspace.active?.files || []).filter(
    (file) => file.parentType === "task" && file.parentId === props.task?.id,
  ),
);
const dependencyCandidates = computed(() => {
  if (!props.task) return [];
  const query = dependencyQuery.value.toLowerCase();
  return (workspace.active?.tasks || []).filter(
    (candidate) =>
      candidate.id !== props.task?.id &&
      candidate.type !== "summary" &&
      !props.task?.predecessorIds.includes(candidate.id) &&
      (props.task?.visibility === "Internal" ||
        candidate.visibility === "Shared") &&
      candidate.title.toLowerCase().includes(query),
  );
});
const blockingTasks = computed(() =>
  (workspace.active?.tasks || []).filter((candidate) =>
    candidate.predecessorIds.includes(props.task?.id || ""),
  ),
);
const visiblePredecessors = computed(() =>
  (props.task?.predecessorIds || []).flatMap((id) => {
    const predecessor = (workspace.active?.tasks || []).find(
      (candidate) => candidate.id === id,
    );
    return predecessor ? [predecessor] : [];
  }),
);
const incompletePredecessors = computed(() => {
  return visiblePredecessors.value.filter(
    (predecessor) =>
      !(["Complete", "Not applicable"] as TaskStatus[]).includes(
        predecessor.status,
      ),
  );
});
const parentCandidates = computed(() =>
  (workspace.active?.tasks || []).filter(
    (candidate) =>
      candidate.id !== props.task?.id &&
      candidate.type === "summary" &&
      candidate.sectionId === draft.sectionId &&
      candidate.visibility === draft.visibility,
  ),
);
const internalOnly = computed({
  get: () => draft.visibility === "Internal",
  set: (value: boolean) => {
    draft.visibility = value ? "Internal" : "Shared";
  },
});
const commentInternalOnly = computed({
  get: () => commentVisibility.value === "Internal",
  set: (value: boolean) => {
    commentVisibility.value = value ? "Internal" : "Shared";
    commentVisibilityChanged();
  },
});
watch([() => draft.sectionId, () => draft.visibility], () => {
  if (
    draft.parentId &&
    !parentCandidates.value.some((candidate) => candidate.id === draft.parentId)
  )
    draft.parentId = "";
});
const blockerIncomplete = computed(
  () =>
    draft.status === "Blocked" &&
    (!draft.blocker?.trim() ||
      (!draft.issueOwnerId && draft.issueOwnerNeeded !== true)),
);
const taskNameError = computed(() => !String(draft.title || "").trim());
const progressEditable = computed(
  () =>
    props.task?.type === "task" &&
    (["In progress", "Blocked", "In review"] as TaskStatus[]).includes(
      draft.status as TaskStatus,
    ),
);
const displayedProgress = computed(() =>
  props.task?.type === "summary"
    ? calculateTaskProgress(
        descendantTasks(workspace.active?.tasks || [], props.task.id),
      ).progress
    : props.task && ["checklist", "questionnaire"].includes(props.task.type)
      ? calculatedWorkItemProgress({
          type: props.task.type,
          progress: Number(draft.progress || 0),
          checklist: draft.checklist || [],
          questions: draft.questions || [],
        })
      : Number(draft.progress || 0),
);
const progressTone = computed(() => {
  if (displayedProgress.value >= 100) return "complete";
  if (displayedProgress.value > 0) return "active";
  return "empty";
});
const workingWeekdays = computed(
  () => workspace.active?.project.workingWeekdays || [1, 2, 3, 4, 5],
);
const projectHolidays = computed(
  () => workspace.active?.project.holidays || [],
);
const calculatedFinish = computed(() => {
  if (!draft.startDate || props.task?.type === "summary") return "";
  if (props.task?.type === "milestone") return String(draft.dueDate || "");
  const duration = Number(draft.duration);
  if (!Number.isInteger(duration) || duration < 1) return "";
  try {
    return calculateProjectFinish(
      draft.startDate,
      duration,
      workingWeekdays.value,
      projectHolidays.value,
    );
  } catch {
    return "";
  }
});
const parentCollection = computed(() =>
  (workspace.active?.tasks || []).find(
    (candidate) =>
      candidate.id === draft.parentId && candidate.type === "summary",
  ),
);
const scheduleError = computed(() => {
  if (!props.task || props.task.type === "summary") return "";
  const project = workspace.active?.project;
  const date =
    props.task.type === "milestone"
      ? String(draft.dueDate || "")
      : String(draft.startDate || "");
  if (!date) return "Choose a date within the project schedule.";
  if (project && (date < project.startDate || date > project.targetEndDate))
    return `Choose a date between ${fullDate(project.startDate)} and ${fullDate(project.targetEndDate)}.`;
  if (!isProjectWorkingDay(date, workingWeekdays.value, projectHolidays.value))
    return "Choose a working day from this project's calendar.";
  if (props.task.type !== "milestone") {
    const duration = Number(draft.duration);
    if (!Number.isInteger(duration) || duration < 1)
      return "Duration must be at least one whole working day.";
    if (
      project?.targetPolicy === "Hard boundary" &&
      calculatedFinish.value > project.targetEndDate
    )
      return `This task would finish after the project target on ${fullDate(project.targetEndDate)}.`;
  }
  return "";
});
const scheduleChanged = computed(() => {
  if (!base.value || !props.task || props.task.type === "summary") return false;
  if (props.task.type === "milestone")
    return draft.dueDate !== base.value.dueDate;
  return (
    draft.startDate !== base.value.startDate ||
    Number(draft.duration) !== Number(base.value.duration)
  );
});
const hasDependencies = computed(() =>
  Boolean(visiblePredecessors.value.length || blockingTasks.value.length),
);
watch(sections, (availableSections) => {
  if (!availableSections.some((section) => section.id === activeSection.value))
    activeSection.value = "overview";
});
const scheduleLocked = computed(
  () =>
    !workspace.canEditPlan ||
    ["Complete", "Not applicable"].includes(String(draft.status)),
);
const requiresReopenReason = computed(() => {
  if (!["Complete", "Not applicable"].includes(String(base.value?.status)))
    return false;
  if (!["Complete", "Not applicable"].includes(String(draft.status)))
    return true;
  if (props.task?.type === "checklist") return displayedProgress.value < 100;
  if (props.task?.type === "questionnaire") {
    const required = (draft.questions || []).filter(
      (question) => question.required !== false,
    );
    return required.some((question) => !questionHasAnswer(question.answer));
  }
  return false;
});

function statusUnavailable(status: TaskStatus): boolean {
  if (status !== "Complete") return false;
  if (props.task?.type === "checklist") return true;
  return (
    props.task?.type === "questionnaire" &&
    (props.task.ownerId !== workspace.user?.id ||
      draft.status !== "In review" ||
      displayedProgress.value !== 100)
  );
}

function conflictValue(field: EditableField, value: unknown) {
  if (field === "ownerId")
    return (
      workspace.active?.participants.find((person) => person.id === value)
        ?.name || "Unassigned"
    );
  if (value === undefined || value === null || value === "") return "Not set";
  return String(value);
}

function resolveConflict(field: EditableField, choice: "mine" | "latest") {
  const conflict = conflicts.value.find((item) => item.field === field);
  if (!conflict) return;
  if (choice === "latest") setDraftField(field, conflict.latest);
  conflicts.value = conflicts.value.filter((item) => item.field !== field);
  if (!conflicts.value.length) saveError.value = "";
}

function issueOwnerStateChanged() {
  if (draft.issueOwnerNeeded) draft.issueOwnerId = "";
}

async function openSaveMenu() {
  saveMenuOpen.value = !saveMenuOpen.value;
  if (!saveMenuOpen.value) return;
  await nextTick();
  saveMenu.value
    ?.querySelector<HTMLButtonElement>("button:not([disabled])")
    ?.focus();
}

function closeSaveMenu(restoreFocus = false) {
  saveMenuOpen.value = false;
  if (restoreFocus) nextTick(() => saveMenuButton.value?.focus());
}

function closeSaveMenuOnFocusOut(event: FocusEvent) {
  const next = event.relatedTarget as Node | null;
  if (next && !saveSplit.value?.contains(next)) closeSaveMenu();
}

function moveInSaveMenu(event: KeyboardEvent) {
  if (!saveMenu.value) return;
  const items = [
    ...saveMenu.value.querySelectorAll<HTMLButtonElement>(
      "button:not([disabled])",
    ),
  ];
  const current = items.indexOf(document.activeElement as HTMLButtonElement);
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    closeSaveMenu(true);
    return;
  }
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  event.preventDefault();
  const next =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? items.length - 1
        : event.key === "ArrowDown"
          ? (current + 1) % items.length
          : (current - 1 + items.length) % items.length;
  items[next]?.focus();
}

async function selectSaveStatus(status: TaskStatus) {
  if (!canChangeStatus.value) return;
  draft.status = status;
  saveError.value = "";
  completionAttempted.value = false;
  closeSaveMenu();
  await focusStatusDetails(status);
}

function requestTaskDeletion() {
  if (!canDeleteTask.value) return;
  closeSaveMenu();
  taskDeleteError.value = "";
  taskDeleteServerBlockers.value = [];
  taskDeleteOpen.value = true;
}

async function confirmTaskDeletion() {
  if (
    !props.task ||
    taskDeleting.value ||
    displayedTaskDeleteBlockers.value.length
  )
    return;
  taskDeleting.value = true;
  taskDeleteError.value = "";
  try {
    await workspace.deleteTasks([props.task.id], "delete");
    taskDeleteOpen.value = false;
    emit("close");
  } catch (error) {
    if (error instanceof ApiError)
      taskDeleteServerBlockers.value = taskDeletionBlockersFromDetails(
        error.details,
      );
    taskDeleteError.value =
      error instanceof Error
        ? error.message
        : "The task was not deleted. Review the task and try again.";
  } finally {
    taskDeleting.value = false;
  }
}

async function focusStatusDetails(status?: TaskStatus) {
  if (status && !canChangeStatus.value) return false;
  if (status === "Blocked") {
    activeSection.value = "overview";
    await nextTick();
    blockerInput.value?.focus();
    return true;
  }
  if (requiresReopenReason.value) {
    activeSection.value = "overview";
    await nextTick();
    reopenReasonInput.value?.focus();
    return true;
  }
  if (status === "Complete" && incompletePredecessors.value.length) {
    activeSection.value = "structure";
    completionAttempted.value = true;
    await nextTick();
    document.querySelector<HTMLElement>(".completion-dependencies")?.focus();
    return true;
  }
  return false;
}

function cancel() {
  if (base.value) replaceDraft(base.value);
  emit("close");
}

function draftPatch(scheduleAlreadyApplied = false) {
  const fields = scheduleAlreadyApplied
    ? editableFields.filter(
        (field) => !["startDate", "dueDate", "duration"].includes(field),
      )
    : editableFields;
  const patch = Object.fromEntries(
    fields
      .filter((field) => !same(draft[field], base.value?.[field]))
      .map((field) => [field, clone(draft[field])]),
  ) as Partial<ProjectTask>;
  if (requiresReopenReason.value)
    patch.reopenReason = draft.reopenReason?.trim();
  return patch;
}

async function persistDraft(scheduleAlreadyApplied = false) {
  if (!props.task || !base.value) return;
  const patch = draftPatch(scheduleAlreadyApplied);
  try {
    const updated = Object.keys(patch).length
      ? await workspace.updateTask(
          props.task.id,
          patch,
          scheduleAlreadyApplied ? props.task.version : base.value.version,
        )
      : props.task;
    replaceDraft(updated);
    emit("saved");
    emit("close");
  } catch (error) {
    saveError.value =
      error instanceof Error
        ? scheduleAlreadyApplied
          ? `The date change was applied, but the other edits were not saved. ${error.message}`
          : error.message
        : "We could not save your changes. Your draft is still open. Try again.";
  }
}

async function applyPendingSchedule(options?: { extensionReason?: string }) {
  if (
    !props.task ||
    !scheduleImpact.value ||
    scheduleImpact.value.canApply === false
  )
    return;
  saving.value = true;
  saveError.value = "";
  try {
    await workspace.applySchedule(scheduleImpact.value, options);
    props.task.duration = Number(draft.duration || props.task.duration);
    draft.startDate = scheduleImpact.value.newStart;
    draft.dueDate = scheduleImpact.value.newDue;
    scheduleImpact.value = null;
    await persistDraft(true);
  } catch (error) {
    saveError.value =
      error instanceof Error
        ? error.message
        : "We could not apply these dates. Your draft is still open. Try again.";
  } finally {
    saving.value = false;
  }
}

async function save() {
  if (!props.task || !base.value || conflicts.value.length) return;
  if (!descriptionValid.value || !notesValid.value) {
    saveError.value =
      "Shorten the highlighted rich-text field before saving. Your draft is still here.";
    return;
  }
  closeSaveMenu();
  completionAttempted.value = false;
  if (taskNameError.value) {
    activeSection.value = "overview";
    saveError.value = "Add a name before saving this work item.";
    return;
  }
  if (draft.status === "Complete" && incompletePredecessors.value.length) {
    activeSection.value = "structure";
    completionAttempted.value = true;
    await nextTick();
    document.querySelector<HTMLElement>(".completion-dependencies")?.focus();
    return;
  }
  if (blockerIncomplete.value) {
    activeSection.value = "overview";
    saveError.value =
      "Describe the blocker and choose an issue owner, or mark that an owner is needed.";
    return;
  }
  if (requiresReopenReason.value && !draft.reopenReason?.trim()) {
    activeSection.value = "overview";
    saveError.value =
      "Add a reason before reopening this completed work item. Your changes are still here.";
    return;
  }
  if (props.task.type === "milestone") {
    draft.startDate = draft.dueDate;
    draft.duration = 0;
    draft.progress = ["Complete", "Not applicable"].includes(
      String(draft.status),
    )
      ? 100
      : 0;
  }
  if (scheduleChanged.value && scheduleError.value) {
    activeSection.value = "overview";
    saveError.value = scheduleError.value;
    return;
  }
  saving.value = true;
  saveError.value = "";
  try {
    if (scheduleChanged.value) {
      const newStart =
        props.task.type === "milestone"
          ? String(draft.dueDate)
          : String(draft.startDate);
      const newDue =
        props.task.type === "milestone"
          ? String(draft.dueDate)
          : calculatedFinish.value;
      draft.dueDate = newDue;
      const impact = await workspace.previewSchedule(
        props.task.id,
        newStart,
        newDue,
      );
      if (!impact) throw new Error("This task is no longer available.");
      scheduleImpact.value = impact;
      if (
        impact.affected.length ||
        impact.crossesBoundary ||
        impact.canApply === false
      ) {
        saving.value = false;
        return;
      }
      await applyPendingSchedule();
      return;
    }
    await persistDraft();
  } catch (error) {
    saveError.value =
      error instanceof Error
        ? error.message
        : "We could not save your changes. Your draft is still open. Try again.";
  } finally {
    if (!scheduleImpact.value) saving.value = false;
  }
}

function addChecklistItem() {
  const label = newChecklistLabel.value.trim();
  if (!label || (draft.checklist?.length || 0) >= 100) return;
  draft.checklist ||= [];
  draft.checklist.push({
    id: `chk_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
    label,
    complete: false,
  });
  newChecklistLabel.value = "";
}

async function requestDeletion(deletion: PendingDeletion, event: MouseEvent) {
  deleteOpener = event.currentTarget as HTMLElement;
  pendingDeletion.value = deletion;
  await nextTick();
  keepDeleteButton.value?.focus();
}

async function cancelDeletion() {
  pendingDeletion.value = null;
  await nextTick();
  deleteOpener?.focus();
}

async function confirmDeletion() {
  const deletion = pendingDeletion.value;
  if (!deletion) return;
  if (deletion.kind === "checklist") {
    draft.checklist =
      draft.checklist?.filter((item) => item.id !== deletion.id) || [];
  } else {
    draft.questions =
      draft.questions?.filter((item) => item.id !== deletion.id) || [];
  }
  pendingDeletion.value = null;
  await nextTick();
  if (deletion.kind === "checklist") newChecklistInput.value?.focus();
  else newQuestionInput.value?.focus();
}

function trapDeleteKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    cancelDeletion();
    return;
  }
  if (event.key !== "Tab" || !deleteDialog.value) return;
  const items = [
    ...deleteDialog.value.querySelectorAll<HTMLButtonElement>(
      "button:not([disabled])",
    ),
  ];
  if (!items.length) return;
  if (event.shiftKey && document.activeElement === items[0]) {
    event.preventDefault();
    items.at(-1)?.focus();
  } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
    event.preventDefault();
    items[0]?.focus();
  }
}

function addQuestion() {
  const label = newQuestionLabel.value.trim();
  if (!label || (draft.questions?.length || 0) >= 100) return;
  const options = newQuestionOptions.value
    .split(",")
    .map((option) => option.trim())
    .filter(Boolean);
  if (
    ["Single select", "Multi-select"].includes(newQuestionType.value) &&
    !options.length
  )
    return;
  draft.questions ||= [];
  draft.questions.push({
    id: `que_${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`,
    label,
    type: newQuestionType.value,
    required: true,
    ...(["Single select", "Multi-select"].includes(newQuestionType.value)
      ? { options }
      : {}),
    answer: "",
  });
  newQuestionLabel.value = "";
  newQuestionType.value = "Short text";
  newQuestionOptions.value = "";
}

function importChecklistItems() {
  const lines = checklistImportLines.value;
  if (!lines.length || checklistImportError.value) return;
  draft.checklist ||= [];
  draft.checklist.push(
    ...lines.map((label) => ({
      id: "chk_" + crypto.randomUUID().replaceAll("-", "").slice(0, 12),
      label,
      complete: false,
    })),
  );
  checklistImportText.value = "";
  checklistImportOpen.value = false;
}

function importQuestions() {
  const lines = questionImportLines.value;
  if (!lines.length || questionImportError.value) return;
  draft.questions ||= [];
  draft.questions.push(
    ...lines.map((label) => ({
      id: "que_" + crypto.randomUUID().replaceAll("-", "").slice(0, 12),
      label,
      type: "Short text" as const,
      required: true,
      answer: "",
    })),
  );
  questionImportText.value = "";
  questionImportOpen.value = false;
}

function changeWorkItemType(event: Event) {
  const type = (event.target as HTMLSelectElement).value as ProjectTask["type"];
  draft.type = type;
  if (
    ["checklist", "questionnaire"].includes(type) &&
    ["Complete", "In progress", "In review"].includes(String(draft.status))
  ) {
    draft.status = "Not started";
    draft.progress = 0;
  }
}

function multiSelectAnswers(answer?: string): string[] {
  return answer ? answer.split("\u001f").filter(Boolean) : [];
}

function toggleQuestionOption(
  question: ProjectTask["questions"][number],
  option: string,
  checked: boolean,
) {
  const selected = new Set(multiSelectAnswers(question.answer));
  if (checked) selected.add(option);
  else selected.delete(option);
  question.answer = [...selected].join("\u001f");
}

async function addDependency(predecessorTaskId: string) {
  if (!props.task) return;
  dependencyError.value = "";
  try {
    await workspace.addDependency(predecessorTaskId, props.task.id);
    dependencyOpen.value = false;
    dependencyQuery.value = "";
  } catch (error) {
    dependencyError.value =
      error instanceof Error
        ? error.message
        : "We could not add this dependency. The schedule has not changed.";
  }
}

async function removeDependency(predecessorTaskId: string) {
  if (!props.task) return;
  dependencyError.value = "";
  try {
    await workspace.removeDependency(predecessorTaskId, props.task.id);
  } catch {
    dependencyError.value =
      "We could not remove this dependency. The schedule has not changed.";
  }
}

function canMention(person: Participant) {
  return (
    person.state !== "pending" &&
    (commentVisibility.value === "Shared" || person.kind === "internal")
  );
}

function commentVisibilityChanged() {
  commentError.value = "";
  commentAudienceMessage.value = "";
  const priorMentions = richTextMentionIds(comment.value);
  const eligibleIds = mentionParticipants.value.map((person) => person.id);
  const next = replaceIneligibleMentions(comment.value, eligibleIds);
  if (richTextMentionIds(next).length !== priorMentions.length) {
    comment.value = next;
    commentAudienceMessage.value =
      "Mentions that are not eligible for this audience were changed to plain text.";
  }
}

async function postComment() {
  if (
    !props.task ||
    richTextIsEmpty(comment.value) ||
    !commentValid.value ||
    commentPosting.value
  )
    return;
  commentPosting.value = true;
  commentError.value = "";
  try {
    await workspace.addComment(
      props.task.id,
      comment.value,
      commentVisibility.value,
      [],
      commentDraftId.value,
    );
    comment.value = emptyRichTextDocument();
    commentDraftId.value = createCommentId();
  } catch (error) {
    const reason =
      error instanceof Error
        ? error.message
        : "We could not add your comment. Try again.";
    commentError.value = `${reason} Your draft is still here.`;
    commentPosting.value = false;
    await nextTick();
    commentEditor.value?.focus();
  } finally {
    commentPosting.value = false;
  }
}

async function uploadTaskImage(file: File) {
  if (!props.task) throw new Error("This task is no longer available.");
  return workspace.uploadPastedImage({
    projectId: props.task.projectId,
    parentType: "task",
    parentId: props.task.id,
    visibility: (draft.visibility || props.task.visibility) as Visibility,
    file,
  });
}

async function uploadCommentImage(file: File) {
  if (!props.task) throw new Error("This task is no longer available.");
  return workspace.uploadPastedImage({
    projectId: props.task.projectId,
    parentType: "comment",
    parentId: commentDraftId.value,
    visibility: commentVisibility.value,
    file,
  });
}
async function uploadTaskFiles(event: Event) {
  const input = event.target;
  if (
    !(input instanceof HTMLInputElement) ||
    !input.files?.length ||
    !props.task
  )
    return;
  taskFileUploading.value = true;
  taskFileError.value = "";
  try {
    for (const file of [...input.files])
      await workspace.uploadFile({
        projectId: props.task.projectId,
        parentType: "task",
        parentId: props.task.id,
        visibility: props.task.visibility,
        file,
      });
  } catch (error) {
    taskFileError.value =
      error instanceof Error
        ? error.message
        : "The file could not be attached. Try again.";
  } finally {
    input.value = "";
    taskFileUploading.value = false;
  }
}
async function downloadTaskFile(fileId: string) {
  if (!props.task || taskFileDownloadingId.value) return;
  taskFileDownloadingId.value = fileId;
  taskFileError.value = "";
  try {
    await workspace.downloadFile(props.task.projectId, fileId);
  } catch (error) {
    taskFileError.value =
      error instanceof Error
        ? error.message
        : "The file could not be downloaded. Try again.";
  } finally {
    taskFileDownloadingId.value = "";
  }
}
function requestFileDeletion(file: TaskFile) {
  artifactDeleteError.value = "";
  commentToDelete.value = null;
  fileToDelete.value = file;
}
function requestCommentDeletion(comment: TaskComment) {
  artifactDeleteError.value = "";
  fileToDelete.value = null;
  commentToDelete.value = comment;
}
function cancelArtifactDeletion() {
  if (artifactDeleting.value) return;
  fileToDelete.value = null;
  commentToDelete.value = null;
  artifactDeleteError.value = "";
}
async function confirmArtifactDeletion() {
  if (!props.task || (!fileToDelete.value && !commentToDelete.value)) return;
  artifactDeleting.value = true;
  artifactDeleteError.value = "";
  try {
    if (fileToDelete.value)
      await workspace.deleteFile(props.task.projectId, fileToDelete.value.id);
    else if (commentToDelete.value)
      await workspace.deleteComment(
        props.task.projectId,
        props.task.id,
        commentToDelete.value.id,
      );
    fileToDelete.value = null;
    commentToDelete.value = null;
  } catch (error) {
    artifactDeleteError.value =
      error instanceof Error
        ? error.message
        : "We could not delete this item. Try again.";
  } finally {
    artifactDeleting.value = false;
  }
}
function trapKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key !== "Tab" || !panel.value) return;
  const focusable = [
    ...panel.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [contenteditable="plaintext-only"], [href], [tabindex]:not([tabindex="-1"])',
    ),
  ];
  if (!focusable.length) return;
  const first = focusable[0]!;
  const last = focusable.at(-1)!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="task-panel">
      <div v-if="open && task" class="task-layer">
        <button
          class="task-scrim"
          aria-label="Close work details"
          @click="emit('close')"
        ></button>
        <section
          ref="panel"
          class="task-panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="`task-title-${task.id}`"
          :aria-hidden="pendingDeletion ? 'true' : undefined"
          :inert="Boolean(pendingDeletion)"
          @keydown="trapKeys"
        >
          <header class="task-panel__header">
            <div class="task-heading">
              <h2 :id="`task-title-${task.id}`">{{ task.title }}</h2>
              <span class="task-kind">{{
                task.type === "milestone"
                  ? "Milestone"
                  : task.type === "summary"
                    ? "Task collection"
                    : task.type === "checklist"
                      ? "Checklist"
                      : task.type === "questionnaire"
                        ? "Questionnaire"
                        : "Task"
              }}</span>
            </div>
            <div class="task-meter" :class="`task-meter--${progressTone}`">
              <div
                class="status-context"
                :class="`status-context--${String(draft.status).toLowerCase().replaceAll(' ', '-')}`"
                :aria-label="`Status: ${draft.status}`"
              >
                <strong>{{ draft.status }}</strong>
              </div>
              <div
                class="progress-summary"
                role="status"
                :aria-label="`Task progress: ${displayedProgress}%`"
              >
                <span class="progress-summary__track" aria-hidden="true">
                  <i :style="{ width: `${displayedProgress}%` }"></i>
                </span>
                <strong class="tabular">{{ displayedProgress }}%</strong>
              </div>
            </div>
            <div class="task-panel__actions">
              <button
                ref="closeButton"
                class="icon-button"
                type="button"
                aria-label="Close work details"
                @click="emit('close')"
              >
                <X :size="21" />
              </button>
            </div>
          </header>
          <nav class="task-tabs" aria-label="Work detail sections">
            <button
              v-for="section in sections"
              :key="section.id"
              type="button"
              :class="{ active: activeSection === section.id }"
              @click="activeSection = section.id"
            >
              {{ section.label }}
            </button>
          </nav>
          <div class="task-panel__body">
            <p
              v-if="!workspace.canEditPlan"
              class="read-only-notice"
              role="status"
            >
              You have view-only access to this project.
            </p>
            <div
              v-if="saveError"
              class="draft-notice"
              :class="{ 'draft-notice--conflict': conflicts.length }"
              role="alert"
            >
              <div>
                <AlertOctagon :size="19" aria-hidden="true" />
                <p>{{ saveError }}</p>
              </div>
              <article
                v-for="conflict in conflicts"
                :key="conflict.field"
                class="draft-conflict"
              >
                <strong>{{ conflict.label }}</strong>
                <dl>
                  <div>
                    <dt>Your draft</dt>
                    <dd>{{ conflictValue(conflict.field, conflict.mine) }}</dd>
                  </div>
                  <div>
                    <dt>Latest saved value</dt>
                    <dd>
                      {{ conflictValue(conflict.field, conflict.latest) }}
                    </dd>
                  </div>
                </dl>
                <div v-if="workspace.canEditPlan">
                  <button
                    class="button button--secondary"
                    type="button"
                    @click="resolveConflict(conflict.field, 'mine')"
                  >
                    Keep mine
                  </button>
                  <button
                    class="button button--secondary"
                    type="button"
                    @click="resolveConflict(conflict.field, 'latest')"
                  >
                    Use latest
                  </button>
                </div>
              </article>
            </div>
            <section
              v-if="incompletePredecessors.length"
              class="completion-dependencies"
              :class="{
                'completion-dependencies--attempted': completionAttempted,
              }"
              :role="completionAttempted ? 'alert' : 'status'"
              :tabindex="completionAttempted ? -1 : undefined"
              aria-labelledby="completion-dependencies-title"
            >
              <AlertOctagon :size="19" aria-hidden="true" />
              <div>
                <strong id="completion-dependencies-title">
                  {{
                    completionAttempted
                      ? `${task.title} can’t be completed yet.`
                      : `Completion depends on ${incompletePredecessors.length} unfinished ${incompletePredecessors.length === 1 ? "predecessor" : "predecessors"}.`
                  }}
                </strong>
                <p>
                  Finish the work below, or mark it Not applicable, before
                  completing this
                  {{ task.type === "milestone" ? "milestone" : "task" }}.
                </p>
                <div class="completion-dependencies__list">
                  <button
                    v-for="predecessor in incompletePredecessors"
                    :key="predecessor.id"
                    type="button"
                    @click="emit('openTask', predecessor.id, 'overview')"
                  >
                    <span>
                      <strong>{{ predecessor.title }}</strong>
                      <small>{{
                        predecessor.type === "milestone" ? "Milestone" : "Task"
                      }}</small>
                    </span>
                    <span class="badge badge--status">{{
                      predecessor.status
                    }}</span>
                  </button>
                </div>
              </div>
            </section>
            <section v-if="activeSection === 'overview'" class="task-section">
              <div class="field">
                <label for="task-title-input">
                  {{
                    task.type === "milestone"
                      ? "Milestone name"
                      : task.type === "summary"
                        ? "Task collection name"
                        : task.type === "checklist"
                          ? "Checklist name"
                          : task.type === "questionnaire"
                            ? "Questionnaire name"
                            : "Task name"
                  }} </label
                ><input
                  id="task-title-input"
                  v-model="draft.title"
                  class="input"
                  :aria-invalid="taskNameError ? 'true' : undefined"
                  :aria-describedby="
                    taskNameError ? 'task-name-error' : undefined
                  "
                />
                <p v-if="taskNameError" id="task-name-error" class="form-error">
                  Add a name for this work item.
                </p>
              </div>

              <div v-if="requiresReopenReason" class="field reopen-reason">
                <label for="work-item-reopen-reason"
                  >Reason for reopening</label
                >
                <textarea
                  id="work-item-reopen-reason"
                  ref="reopenReasonInput"
                  v-model="draft.reopenReason"
                  class="textarea"
                  maxlength="500"
                  required
                  placeholder="Explain why this completed work item needs more work."
                ></textarea>
                <p class="helper">
                  This reason is recorded in project activity for context.
                </p>
              </div>

              <section
                class="overview-group"
                aria-labelledby="overview-schedule-title"
              >
                <div class="overview-group__heading">
                  <h3 id="overview-schedule-title">Schedule</h3>
                  <p class="muted">
                    {{
                      task.type === "milestone"
                        ? "Set the date when this checkpoint must be reached."
                        : task.type === "summary"
                          ? "Dates are calculated from the child work in this collection."
                          : "Choose a start and the number of working days needed. The finish date is calculated for you."
                    }}
                  </p>
                </div>
                <div v-if="task.type === 'summary'" class="derived-schedule">
                  <div>
                    <span>Calculated start</span>
                    <strong>{{
                      task.startDate
                        ? fullDate(task.startDate)
                        : "Not scheduled"
                    }}</strong>
                  </div>
                  <div>
                    <span>Calculated finish</span>
                    <strong>{{
                      task.dueDate ? fullDate(task.dueDate) : "Not scheduled"
                    }}</strong>
                  </div>
                </div>
                <div v-else-if="task.type === 'milestone'" class="field">
                  <label for="milestone-target">Target date</label>
                  <input
                    id="milestone-target"
                    v-model="draft.dueDate"
                    class="input"
                    type="date"
                    :min="workspace.active?.project.startDate"
                    :max="workspace.active?.project.targetEndDate"
                    :disabled="scheduleLocked"
                    :aria-invalid="scheduleError ? 'true' : undefined"
                  />
                  <p class="helper">
                    Uses the project's working calendar and schedule boundary.
                  </p>
                </div>
                <div v-else class="schedule-fields">
                  <div class="field">
                    <label for="task-start">Start date</label>
                    <input
                      id="task-start"
                      v-model="draft.startDate"
                      class="input"
                      type="date"
                      :min="workspace.active?.project.startDate"
                      :max="workspace.active?.project.targetEndDate"
                      :disabled="scheduleLocked"
                      :aria-invalid="scheduleError ? 'true' : undefined"
                    />
                  </div>
                  <div class="field">
                    <label for="duration">Duration</label>
                    <div class="input-with-unit">
                      <input
                        id="duration"
                        v-model.number="draft.duration"
                        class="input"
                        type="number"
                        min="1"
                        step="1"
                        inputmode="numeric"
                        :disabled="scheduleLocked"
                        :aria-invalid="scheduleError ? 'true' : undefined"
                      /><span>working days</span>
                    </div>
                  </div>
                  <div class="calculated-finish" aria-live="polite">
                    <span>Calculated finish</span>
                    <strong>{{
                      calculatedFinish
                        ? fullDate(calculatedFinish)
                        : "Choose a valid start and duration"
                    }}</strong>
                  </div>
                  <p class="schedule-scope">
                    <template v-if="parentCollection">
                      {{ parentCollection.title }} updates from the dates of its
                      child work.
                    </template>
                    <template v-else>
                      This task must stay within the project schedule.
                    </template>
                  </p>
                </div>
                <p
                  v-if="scheduleError && task.type !== 'summary'"
                  class="form-error"
                  role="alert"
                >
                  {{ scheduleError }}
                </p>
              </section>

              <div v-if="progressEditable" class="field">
                <div class="progress-label-row">
                  <label id="task-progress-label" for="task-progress"
                    >Progress</label
                  ><output for="task-progress" class="progress-value tabular"
                    >{{ draft.progress ?? 0 }}%</output
                  >
                </div>
                <input
                  id="task-progress"
                  v-model.number="draft.progress"
                  class="progress-slider"
                  type="range"
                  min="0"
                  max="95"
                  step="5"
                  :aria-valuetext="`${draft.progress ?? 0}% complete`"
                  aria-describedby="task-progress-help"
                />
                <p id="task-progress-help" class="helper">
                  Adjust in 5% steps. Mark the task Complete or Not applicable
                  to set 100%.
                </p>
              </div>

              <div class="optional-description">
                <button
                  v-if="!descriptionExpanded"
                  class="optional-description__add"
                  type="button"
                  @click="descriptionExpanded = true"
                >
                  <Plus :size="16" aria-hidden="true" />
                  <span>
                    <strong>Add description</strong>
                    <small
                      >Optional context, outcome, or acceptance criteria</small
                    >
                  </span>
                </button>
                <div v-else class="field">
                  <span class="field-label"
                    >Description <small>Optional</small></span
                  >
                  <RichTextEditor
                    v-model="descriptionDocument"
                    label="Task description"
                    :participants="taskMentionParticipants"
                    :maxlength="5000"
                    placeholder="Describe the outcome, context, or acceptance criteria for this work."
                    :upload-pasted-image="
                      workspace.isInternal ? uploadTaskImage : undefined
                    "
                    @update:plain-text="draft.description = $event"
                    @update:valid="descriptionValid = $event"
                  />
                </div>
              </div>

              <div v-if="draft.status === 'Blocked'" class="blocker-form">
                <AlertOctagon :size="20" />
                <div class="field">
                  <label for="blocker">What is preventing progress?</label
                  ><textarea
                    id="blocker"
                    ref="blockerInput"
                    v-model="draft.blocker"
                    class="textarea"
                    placeholder="Describe the blocker and what is needed to move forward."
                  ></textarea>
                </div>
                <div class="field">
                  <label for="issue-owner"
                    >Who is responsible for the issue?</label
                  >
                  <select
                    id="issue-owner"
                    v-model="draft.issueOwnerId"
                    class="select"
                    :disabled="draft.issueOwnerNeeded === true"
                  >
                    <option value="">Choose an issue owner</option>
                    <option
                      v-for="person in workspace.active?.participants.filter(
                        (item) =>
                          draft.visibility !== 'Internal' ||
                          item.kind === 'internal',
                      )"
                      :key="person.id"
                      :value="person.id"
                    >
                      {{ person.name }} · {{ person.organization }}
                    </option>
                  </select>
                  <label class="checkbox-row">
                    <input
                      v-model="draft.issueOwnerNeeded"
                      type="checkbox"
                      @change="issueOwnerStateChanged"
                    />
                    An issue owner still needs to be assigned
                  </label>
                  <p v-if="blockerIncomplete" class="form-error">
                    A blocker needs a description and a responsible owner state.
                  </p>
                </div>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'structure'"
              class="task-section"
            >
              <div v-if="workspace.canEditPlan" class="overview-group__heading">
                <h3>Structure</h3>
                <p class="muted">
                  Place this work in the plan, assign responsibility, and
                  control who can see it.
                </p>
              </div>
              <div
                v-if="
                  workspace.canEditPlan &&
                  ['task', 'checklist', 'questionnaire'].includes(task.type)
                "
                class="field work-item-type-field"
              >
                <label for="work-item-type">Work-item type</label>
                <select
                  id="work-item-type"
                  :value="draft.type"
                  class="select"
                  @change="changeWorkItemType"
                >
                  <option value="task">Task</option>
                  <option value="checklist">Checklist</option>
                  <option value="questionnaire">Questionnaire</option>
                </select>
                <p v-if="draft.type !== task.type" class="conversion-notice">
                  <template v-if="task.type === 'checklist'">
                    Checklist items and their checked state will be appended to
                    the description before this becomes a
                    {{ draft.type === "task" ? "task" : "questionnaire" }}.
                  </template>
                  <template v-else-if="task.type === 'questionnaire'">
                    Questions and existing answers will be appended to the
                    description before this becomes a
                    {{ draft.type === "task" ? "task" : "checklist" }}.
                  </template>
                  <template v-else>
                    This task will become a
                    {{
                      draft.type === "checklist"
                        ? "checklist"
                        : "questionnaire"
                    }}. Existing Notes will be appended to the description.
                  </template>
                  <template
                    v-if="
                      ['checklist', 'questionnaire'].includes(
                        String(draft.type),
                      )
                    "
                  >
                    It starts empty, so progress and status are recalculated
                    from its contents.
                  </template>
                </p>
              </div>
              <div v-if="workspace.canEditPlan" class="form-row">
                <div class="field">
                  <label for="task-section">Section</label>
                  <select
                    id="task-section"
                    v-model="draft.sectionId"
                    class="select"
                  >
                    <option
                      v-for="section in workspace.active?.sections.filter(
                        (item) =>
                          draft.visibility !== 'Shared' ||
                          item.visibility !== 'Internal',
                      )"
                      :key="section.id"
                      :value="section.id"
                    >
                      {{ section.name }}
                    </option>
                  </select>
                </div>
                <div v-if="task.type !== 'summary'" class="field">
                  <label for="task-owner">Owner</label>
                  <select
                    id="task-owner"
                    v-model="draft.ownerId"
                    class="select"
                  >
                    <option value="">Unassigned</option>
                    <option
                      v-for="person in taskMentionParticipants"
                      :key="person.id"
                      :value="person.id"
                    >
                      {{ person.name }} · {{ person.organization }}
                    </option>
                  </select>
                </div>
              </div>
              <div
                v-if="workspace.canEditPlan && task.type !== 'summary'"
                class="field"
              >
                <label for="task-parent">Task collection</label>
                <select
                  id="task-parent"
                  v-model="draft.parentId"
                  class="select"
                >
                  <option value="">No task collection</option>
                  <option v-if="!parentCandidates.length" disabled>
                    No matching task collections
                  </option>
                  <option
                    v-for="candidate in parentCandidates"
                    :key="candidate.id"
                    :value="candidate.id"
                  >
                    {{ candidate.title }}
                  </option>
                </select>
                <p class="helper">
                  <template v-if="parentCandidates.length">
                    Only collections in this section with the same access are
                    listed.
                  </template>
                  <template v-else>
                    No collections match this task's section and access.
                  </template>
                </p>
              </div>
              <label
                v-if="workspace.canManageProject"
                class="checkbox-row internal-only-toggle"
                for="task-internal-only"
              >
                <input
                  id="task-internal-only"
                  v-model="internalOnly"
                  type="checkbox"
                />
                <span
                  ><strong>Internal</strong
                  ><small
                    >Only eligible VIPdesk project participants can see this
                    work.</small
                  ></span
                >
              </label>
              <div
                v-if="
                  task.type !== 'summary' &&
                  (workspace.canEditPlan || hasDependencies)
                "
                class="dependency-list"
              >
                <div>
                  <h3>Dependencies</h3>
                  <button
                    v-if="workspace.canEditPlan && !scheduleLocked"
                    class="button button--secondary"
                    type="button"
                    :aria-expanded="dependencyOpen"
                    @click="dependencyOpen = !dependencyOpen"
                  >
                    <Plus :size="15" /> Add dependency
                  </button>
                </div>
                <div v-if="dependencyOpen" class="dependency-picker">
                  <label for="dependency-search">This task is blocked by</label>
                  <input
                    id="dependency-search"
                    v-model="dependencyQuery"
                    class="input"
                    placeholder="Find a task or milestone"
                    autocomplete="off"
                  />
                  <div role="listbox" aria-label="Dependency options">
                    <button
                      v-for="candidate in dependencyCandidates"
                      :key="candidate.id"
                      type="button"
                      role="option"
                      @click="addDependency(candidate.id)"
                    >
                      <strong>{{ candidate.title }}</strong>
                      <small>{{
                        candidate.type === "milestone" ? "Milestone" : "Task"
                      }}</small>
                    </button>
                    <p v-if="!dependencyCandidates.length" class="muted">
                      No available tasks or milestones match this search.
                    </p>
                  </div>
                </div>
                <p v-if="dependencyError" class="form-error" role="alert">
                  {{ dependencyError }}
                </p>
                <div v-if="visiblePredecessors.length" class="dependency-group">
                  <strong class="dependency-heading">Blocked by</strong>
                  <div
                    v-for="predecessor in visiblePredecessors"
                    :key="predecessor.id"
                    class="dependency"
                  >
                    <Link2 :size="16" /><button
                      class="dependency__task"
                      type="button"
                      @click="emit('openTask', predecessor.id, 'overview')"
                    >
                      <strong>{{ predecessor.title }}</strong>
                      <small>
                        Finish to start · {{ predecessor.status }}
                      </small></button
                    ><button
                      v-if="workspace.canEditPlan && !scheduleLocked"
                      class="icon-button"
                      type="button"
                      :aria-label="`Remove dependency on ${predecessor.title}`"
                      @click="removeDependency(predecessor.id)"
                    >
                      <Trash2 :size="16" />
                    </button>
                  </div>
                </div>
                <div v-if="blockingTasks.length" class="blocking-list">
                  <strong>Blocking</strong>
                  <span v-for="blocked in blockingTasks" :key="blocked.id">
                    {{ blocked.title }}
                  </span>
                </div>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'notes' && task.type === 'task'"
              class="task-section"
            >
              <div>
                <h3>Notes</h3>
                <p class="muted">
                  Keep working details that are useful for this task but do not
                  belong in its outcome description.
                </p>
              </div>
              <div class="field">
                <span class="field-label">Task notes</span>
                <RichTextEditor
                  v-model="notesDocument"
                  label="Task notes"
                  :participants="taskMentionParticipants"
                  :maxlength="20000"
                  placeholder="Document setup details, decisions, references, or other task-specific information."
                  :upload-pasted-image="
                    workspace.isInternal ? uploadTaskImage : undefined
                  "
                  @update:plain-text="draft.notes = $event"
                  @update:valid="notesValid = $event"
                />
                <p class="helper">These notes inherit this task's access.</p>
              </div>
            </section>

            <section
              v-else-if="
                activeSection === 'responses' && task.type === 'checklist'
              "
              class="task-section"
            >
              <div class="section-heading">
                <div>
                  <h3>Checklist items</h3>
                  <p class="muted">
                    {{
                      draft.checklist?.filter((item) => item.complete).length ||
                      0
                    }}
                    of {{ draft.checklist?.length || 0 }} checked. Checking the
                    final item completes this checklist automatically.
                  </p>
                </div>
                <form
                  v-if="workspace.canEditPlan"
                  class="inline-builder"
                  @submit.prevent="addChecklistItem"
                >
                  <input
                    ref="newChecklistInput"
                    v-model="newChecklistLabel"
                    class="input"
                    aria-label="New checklist item"
                    placeholder="Add a checklist item"
                  />
                  <button
                    class="button button--secondary"
                    type="submit"
                    :disabled="
                      !newChecklistLabel.trim() ||
                      (draft.checklist?.length || 0) >= 100
                    "
                  >
                    <Plus :size="15" /> Add item
                  </button>
                </form>
              </div>
              <div v-if="workspace.canEditPlan" class="item-import">
                <button
                  class="button button--quiet"
                  type="button"
                  :aria-expanded="checklistImportOpen"
                  @click="checklistImportOpen = !checklistImportOpen"
                >
                  <ClipboardPaste :size="15" /> Import checklist items
                </button>
                <form
                  v-if="checklistImportOpen"
                  class="item-import__form"
                  @submit.prevent="importChecklistItems"
                >
                  <label for="checklist-import"
                    >One checklist item per line</label
                  >
                  <textarea
                    id="checklist-import"
                    v-model="checklistImportText"
                    class="textarea"
                    placeholder="Confirm attendee list&#10;Review training materials&#10;Send final reminder"
                  ></textarea>
                  <span class="helper"
                    >Imported items are added unchecked. Existing items stay in
                    place.</span
                  >
                  <p
                    v-if="checklistImportError"
                    class="form-error"
                    role="alert"
                  >
                    {{ checklistImportError }}
                  </p>
                  <div>
                    <button
                      class="button button--quiet"
                      type="button"
                      @click="checklistImportOpen = false"
                    >
                      Cancel
                    </button>
                    <button
                      class="button button--secondary"
                      type="submit"
                      :disabled="
                        !checklistImportLines.length || !!checklistImportError
                      "
                    >
                      Add items
                    </button>
                  </div>
                </form>
              </div>
              <div v-if="draft.checklist?.length" class="checklist">
                <div
                  v-for="item in draft.checklist"
                  :key="item.id"
                  class="checklist-row"
                >
                  <label
                    ><input v-model="item.complete" type="checkbox" /><span
                      :class="{ done: item.complete }"
                      >{{ item.label }}</span
                    ></label
                  >
                  <button
                    v-if="workspace.canEditPlan"
                    class="icon-button"
                    type="button"
                    :aria-label="`Delete ${item.label}`"
                    @click="
                      requestDeletion(
                        { kind: 'checklist', id: item.id, label: item.label },
                        $event,
                      )
                    "
                  >
                    <Trash2 :size="15" />
                  </button>
                </div>
              </div>
              <div v-else class="empty-block">
                <CheckSquare2 :size="23" /><strong
                  >No checklist items yet.</strong
                ><span>Add the steps required to complete this checklist.</span>
              </div>
              <div v-if="requiresReopenReason" class="field reopen-reason">
                <label for="checklist-reopen-reason"
                  >Reason for reopening</label
                >
                <textarea
                  id="checklist-reopen-reason"
                  v-model="draft.reopenReason"
                  class="textarea"
                  maxlength="500"
                  required
                  placeholder="Explain why this completed checklist needs more work."
                ></textarea>
              </div>
            </section>

            <section
              v-else-if="
                activeSection === 'responses' && task.type === 'questionnaire'
              "
              class="task-section"
            >
              <div class="section-heading">
                <div>
                  <h3>Questions and answers</h3>
                  <p class="muted">
                    {{
                      draft.questions?.filter(
                        (question) =>
                          question.required !== false &&
                          questionHasAnswer(question.answer),
                      ).length || 0
                    }}
                    of
                    {{
                      draft.questions?.filter(
                        (question) => question.required !== false,
                      ).length || 0
                    }}
                    required questions answered. The final required answer moves
                    this questionnaire to In review.
                  </p>
                </div>
                <form
                  v-if="workspace.canEditPlan"
                  class="inline-builder inline-builder--question"
                  @submit.prevent="addQuestion"
                >
                  <input
                    ref="newQuestionInput"
                    v-model="newQuestionLabel"
                    class="input"
                    aria-label="New question"
                    placeholder="Add a question"
                  />
                  <select
                    v-model="newQuestionType"
                    class="select"
                    aria-label="Question type"
                  >
                    <option>Short text</option>
                    <option>Long text</option>
                    <option>Yes or no</option>
                    <option>Single select</option>
                    <option>Multi-select</option>
                    <option>Date</option>
                    <option>Number</option>
                    <option>URL</option>
                    <option>File request</option>
                  </select>
                  <input
                    v-if="
                      ['Single select', 'Multi-select'].includes(
                        newQuestionType,
                      )
                    "
                    v-model="newQuestionOptions"
                    class="input"
                    aria-label="Answer options"
                    placeholder="Options separated by commas"
                  />
                  <button
                    class="button button--secondary"
                    type="submit"
                    :disabled="
                      !newQuestionLabel.trim() ||
                      (draft.questions?.length || 0) >= 100 ||
                      (['Single select', 'Multi-select'].includes(
                        newQuestionType,
                      ) &&
                        !newQuestionOptions.trim())
                    "
                  >
                    <Plus :size="15" /> Add question
                  </button>
                </form>
              </div>
              <div v-if="workspace.canEditPlan" class="item-import">
                <button
                  class="button button--quiet"
                  type="button"
                  :aria-expanded="questionImportOpen"
                  @click="questionImportOpen = !questionImportOpen"
                >
                  <ClipboardPaste :size="15" /> Import questions
                </button>
                <form
                  v-if="questionImportOpen"
                  class="item-import__form"
                  @submit.prevent="importQuestions"
                >
                  <label for="question-import">One question per line</label>
                  <textarea
                    id="question-import"
                    v-model="questionImportText"
                    class="textarea"
                    placeholder="Who approves the final schedule?&#10;What reporting cadence is required?"
                  ></textarea>
                  <span class="helper"
                    >Imported questions are Required short-text questions.
                    Existing questions and answers stay in place.</span
                  >
                  <p v-if="questionImportError" class="form-error" role="alert">
                    {{ questionImportError }}
                  </p>
                  <div>
                    <button
                      class="button button--quiet"
                      type="button"
                      @click="questionImportOpen = false"
                    >
                      Cancel
                    </button>
                    <button
                      class="button button--secondary"
                      type="submit"
                      :disabled="
                        !questionImportLines.length || !!questionImportError
                      "
                    >
                      Add questions
                    </button>
                  </div>
                </form>
              </div>
              <div v-if="draft.questions?.length" class="questions">
                <div
                  v-for="question in draft.questions"
                  :key="question.id"
                  class="field"
                >
                  <div class="question-label-row">
                    <label :id="`${question.id}-label`" :for="question.id"
                      >{{ question.label }}
                      <span v-if="question.required !== false">Required</span
                      ><span v-else>Optional</span></label
                    >
                    <div v-if="workspace.canEditPlan" class="question-actions">
                      <label class="optional-toggle"
                        ><input v-model="question.required" type="checkbox" />
                        Required</label
                      >
                      <button
                        class="question-remove icon-button"
                        type="button"
                        :aria-label="`Delete ${question.label}`"
                        @click="
                          requestDeletion(
                            {
                              kind: 'question',
                              id: question.id,
                              label: question.label,
                            },
                            $event,
                          )
                        "
                      >
                        <Trash2 :size="15" />
                      </button>
                    </div>
                  </div>
                  <p v-if="question.helperText" class="helper">
                    {{ question.helperText }}
                  </p>
                  <textarea
                    v-if="question.type === 'Long text'"
                    :id="question.id"
                    v-model="question.answer"
                    class="textarea"
                  ></textarea
                  ><select
                    v-else-if="question.type === 'Yes or no'"
                    :id="question.id"
                    v-model="question.answer"
                    class="select"
                  >
                    <option value="">Choose an answer</option>
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                  <select
                    v-else-if="question.type === 'Single select'"
                    :id="question.id"
                    v-model="question.answer"
                    class="select"
                  >
                    <option value="">Choose an answer</option>
                    <option
                      v-for="option in question.options || []"
                      :key="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                  <div
                    v-else-if="question.type === 'Multi-select'"
                    class="question-options"
                    role="group"
                    :aria-labelledby="`${question.id}-label`"
                  >
                    <label
                      v-for="option in question.options || []"
                      :key="option"
                    >
                      <input
                        type="checkbox"
                        :checked="
                          multiSelectAnswers(question.answer).includes(option)
                        "
                        @change="
                          toggleQuestionOption(
                            question,
                            option,
                            ($event.target as HTMLInputElement).checked,
                          )
                        "
                      />
                      {{ option }}
                    </label>
                  </div>
                  <input
                    v-else
                    :id="question.id"
                    v-model="question.answer"
                    class="input"
                    :type="
                      question.type === 'Date'
                        ? 'date'
                        : question.type === 'Number'
                          ? 'number'
                          : question.type === 'URL'
                            ? 'url'
                            : 'text'
                    "
                    :placeholder="
                      question.type === 'File request'
                        ? 'Record the requested file or link'
                        : undefined
                    "
                  />
                </div>
              </div>
              <div v-else class="empty-block">
                <MessageSquareText :size="23" /><strong
                  >No questions yet.</strong
                ><span
                  >Add the information this questionnaire needs to
                  collect.</span
                >
              </div>
              <div v-if="requiresReopenReason" class="field reopen-reason">
                <label for="questionnaire-reopen-reason"
                  >Reason for reopening</label
                >
                <textarea
                  id="questionnaire-reopen-reason"
                  v-model="draft.reopenReason"
                  class="textarea"
                  maxlength="500"
                  required
                  placeholder="Explain why this completed questionnaire needs another answer."
                ></textarea>
              </div>
            </section>

            <section v-else-if="activeSection === 'files'" class="task-section">
              <div class="section-heading">
                <div>
                  <h3>Files</h3>
                  <p v-if="workspace.isInternal" class="muted">
                    Files inherit this work item's access when attached. Marking
                    the work Internal also narrows its existing attachments.
                  </p>
                  <p v-else class="muted">
                    Files connected to this work appear here when available.
                  </p>
                </div>
                <div v-if="workspace.isInternal">
                  <input
                    ref="taskFileInput"
                    class="sr-only"
                    type="file"
                    multiple
                    aria-label="Choose task files to attach"
                    accept=".pdf,.txt,.csv,.docx,.xlsx,.pptx,.png,.jpg,.jpeg"
                    @change="uploadTaskFiles"
                  />
                  <button
                    class="button button--secondary"
                    type="button"
                    :disabled="taskFileUploading"
                    @click="taskFileInput?.click()"
                  >
                    <Upload :size="15" />
                    {{ taskFileUploading ? "Uploading…" : "Attach files" }}
                  </button>
                </div>
              </div>
              <p v-if="taskFileError" class="form-error" role="alert">
                {{ taskFileError }}
              </p>
              <div v-for="file in taskFiles" :key="file.id" class="file-row">
                <span class="file-icon"><FileText :size="20" /></span
                ><span
                  ><strong>{{ file.name }}</strong
                  ><small
                    >{{ file.size }} · {{ file.availabilityState
                    }}<span
                      v-if="
                        workspace.isInternal && file.visibility === 'Internal'
                      "
                      title="Internal"
                    >
                      · <EyeOff :size="12" aria-hidden="true" /> Internal</span
                    ></small
                  ></span
                ><button
                  v-if="file.availabilityState === 'Available'"
                  class="file-row__action"
                  type="button"
                  :disabled="Boolean(taskFileDownloadingId)"
                  :aria-label="`Download ${file.name}`"
                  @click="downloadTaskFile(file.id)"
                >
                  <Download :size="16" />
                </button>
                <span v-else class="file-row__state">
                  {{ file.availabilityState }}
                </span>
                <button
                  v-if="workspace.canManageProject"
                  class="file-row__action file-row__action--delete"
                  type="button"
                  :aria-label="`Delete ${file.name}`"
                  @click="requestFileDeletion(file)"
                >
                  <Trash2 :size="16" />
                </button>
              </div>
              <div v-if="!taskFiles.length" class="empty-block">
                <FileText :size="23" /><strong>No files attached.</strong
                ><span>{{
                  workspace.isInternal
                    ? "Attach supporting material for this work item."
                    : "Files attached to this work item will appear here."
                }}</span>
              </div>
            </section>

            <section v-else class="task-section comments-section">
              <div v-if="task.comments.length" class="thread">
                <article
                  v-for="entry in task.comments"
                  :key="entry.id"
                  class="comment"
                >
                  <PersonAvatar
                    :name="entry.author"
                    :initials="entry.initials"
                    :photo-url="entry.photoUrl"
                  />
                  <div>
                    <header>
                      <strong>{{ entry.author }}</strong
                      ><time>{{ entry.createdAt }}</time
                      ><span
                        v-if="
                          workspace.isInternal &&
                          entry.visibility === 'Internal'
                        "
                        class="badge badge--internal"
                        title="Internal comment"
                        ><EyeOff :size="11" aria-hidden="true" />Internal</span
                      >
                      <button
                        v-if="workspace.canManageProject && !entry.deleted"
                        class="comment-delete-button"
                        type="button"
                        :aria-label="`Delete comment by ${entry.author}`"
                        @click="requestCommentDeletion(entry)"
                      >
                        <Trash2 :size="14" />
                      </button>
                    </header>
                    <p v-if="entry.deleted" class="comment-deleted">
                      Comment deleted.
                    </p>
                    <RichTextRenderer
                      v-else
                      :document="entry.bodyRichText"
                      :plain-text="entry.body"
                      :participants="workspace.active?.participants || []"
                      compact
                    />
                    <article
                      v-for="reply in entry.replies"
                      :key="reply.id"
                      class="comment comment--reply"
                    >
                      <PersonAvatar
                        :name="reply.author"
                        :initials="reply.initials"
                        :photo-url="reply.photoUrl"
                      />
                      <div>
                        <header>
                          <strong>{{ reply.author }}</strong
                          ><time>{{ reply.createdAt }}</time
                          ><button
                            v-if="workspace.canManageProject && !reply.deleted"
                            class="comment-delete-button"
                            type="button"
                            :aria-label="`Delete comment by ${reply.author}`"
                            @click="requestCommentDeletion(reply)"
                          >
                            <Trash2 :size="14" />
                          </button>
                        </header>
                        <p v-if="reply.deleted" class="comment-deleted">
                          Comment deleted.
                        </p>
                        <RichTextRenderer
                          v-else
                          :document="reply.bodyRichText"
                          :plain-text="reply.body"
                          :participants="workspace.active?.participants || []"
                          compact
                        />
                      </div>
                    </article>
                  </div>
                </article>
              </div>
              <div v-else class="empty-block">
                <MessageSquareText :size="23" /><strong>No comments yet.</strong
                ><span
                  >Start a thread when the team needs a decision or durable
                  context.</span
                >
              </div>
              <form class="comment-composer" @submit.prevent="postComment">
                <RichTextEditor
                  ref="commentEditor"
                  v-model="comment"
                  profile="comment"
                  label="Add a comment"
                  :participants="mentionParticipants"
                  :disabled="commentPosting"
                  :invalid="Boolean(commentError)"
                  :described-by="commentError ? 'comment-error' : undefined"
                  :maxlength="10000"
                  :upload-pasted-image="
                    workspace.isInternal ? uploadCommentImage : undefined
                  "
                  @update:valid="commentValid = $event"
                />
                <div class="comment-composer__actions">
                  <label
                    v-if="workspace.isInternal && task.visibility === 'Shared'"
                    class="internal-comment-option"
                  >
                    <input v-model="commentInternalOnly" type="checkbox" />
                    <span>Internal comment</span>
                  </label>
                  <button
                    class="button button--primary"
                    type="submit"
                    :disabled="
                      commentPosting ||
                      richTextIsEmpty(comment) ||
                      !commentValid
                    "
                  >
                    <Send :size="15" />
                    {{ commentPosting ? "Adding comment…" : "Add comment" }}
                  </button>
                </div>
                <p v-if="commentAudienceMessage" class="helper" role="status">
                  {{ commentAudienceMessage }}
                </p>
                <p
                  v-if="commentError"
                  id="comment-error"
                  class="form-error comment-error"
                  role="alert"
                >
                  {{ commentError }}
                </p>
              </form>
            </section>
          </div>
          <footer class="task-panel__footer">
            <button
              class="button button--secondary footer-cancel"
              type="button"
              :disabled="saving"
              @click="cancel"
            >
              Cancel
            </button>
            <span v-if="saving" class="muted footer-state">
              Saving changes…
            </span>
            <span v-else-if="conflicts.length" class="conflict-state">
              Resolve {{ conflicts.length }} overlapping
              {{ conflicts.length === 1 ? "change" : "changes" }}
            </span>
            <span v-else-if="dirty" class="muted">Unsaved changes</span>
            <span v-else></span>
            <div
              v-if="canChangeStatus"
              ref="saveSplit"
              class="save-split"
              @focusout="closeSaveMenuOnFocusOut"
              @keydown="moveInSaveMenu"
            >
              <button
                class="button button--primary save-split__main"
                type="button"
                :disabled="
                  !dirty ||
                  saving ||
                  !!conflicts.length ||
                  blockerIncomplete ||
                  taskNameError ||
                  !descriptionValid ||
                  !notesValid
                "
                @click="save()"
              >
                {{ saving ? "Saving…" : `Save as ${draft.status}` }}
              </button>
              <button
                ref="saveMenuButton"
                class="button button--primary save-split__toggle"
                type="button"
                :aria-label="`Choose status. Current status: ${draft.status}`"
                aria-haspopup="menu"
                :aria-expanded="saveMenuOpen"
                :disabled="
                  saving ||
                  !!conflicts.length ||
                  taskNameError ||
                  !canChangeStatus
                "
                @click="openSaveMenu"
              >
                <ChevronDown :size="16" aria-hidden="true" />
              </button>
              <div
                v-if="saveMenuOpen"
                ref="saveMenu"
                class="save-status-menu"
                role="menu"
                aria-label="Choose status"
              >
                <p>Save status</p>
                <button
                  v-for="status in statusOptions"
                  :key="status"
                  type="button"
                  role="menuitem"
                  :aria-current="status === draft.status ? 'true' : undefined"
                  :disabled="statusUnavailable(status)"
                  @click="selectSaveStatus(status)"
                >
                  <span class="save-status-menu__label">
                    <i
                      class="status-swatch"
                      :class="`status-swatch--${status.toLowerCase().replaceAll(' ', '-')}`"
                      aria-hidden="true"
                    ></i>
                    {{ status }}
                  </span>
                  <Check
                    v-if="status === draft.status"
                    :size="15"
                    aria-hidden="true"
                  />
                </button>
                <button
                  v-if="canDeleteTask"
                  class="save-status-menu__delete"
                  type="button"
                  role="menuitem"
                  @click="requestTaskDeletion"
                >
                  <span class="save-status-menu__label">
                    <Trash2 :size="15" aria-hidden="true" />
                    Delete Task
                  </span>
                </button>
              </div>
            </div>
            <div v-else class="footer-actions">
              <button
                v-if="canDeleteTask && task?.type === 'summary'"
                class="button button--danger"
                type="button"
                :disabled="saving"
                @click="requestTaskDeletion"
              >
                Delete task collection
              </button>
              <button
                class="button button--primary footer-save"
                type="button"
                :disabled="
                  !dirty ||
                  !workspace.canEditPlan ||
                  saving ||
                  !!conflicts.length ||
                  taskNameError ||
                  !descriptionValid ||
                  !notesValid
                "
                @click="save()"
              >
                {{ saving ? "Saving…" : "Save and close" }}
              </button>
            </div>
          </footer>
        </section>
      </div>
    </Transition>
    <div v-if="pendingDeletion" class="delete-layer">
      <button
        class="delete-scrim"
        type="button"
        :aria-label="
          pendingDeletion.kind === 'checklist' ? 'Keep item' : 'Keep question'
        "
        @click="cancelDeletion"
      ></button>
      <section
        ref="deleteDialog"
        class="delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        @keydown="trapDeleteKeys"
      >
        <header>
          <span class="delete-dialog__icon" aria-hidden="true">
            <Trash2 :size="20" />
          </span>
          <div>
            <h2 id="delete-dialog-title">
              {{
                pendingDeletion.kind === "checklist"
                  ? "Delete checklist item?"
                  : "Delete question?"
              }}
            </h2>
            <p id="delete-dialog-description">
              <strong>“{{ pendingDeletion.label }}”</strong>
              {{
                pendingDeletion.kind === "checklist"
                  ? "will be removed from this checklist."
                  : "and its answer will be removed from this questionnaire."
              }}
              This change is not saved until you save the
              {{
                pendingDeletion.kind === "checklist"
                  ? "checklist"
                  : "questionnaire"
              }}.
            </p>
          </div>
        </header>
        <footer>
          <button
            ref="keepDeleteButton"
            class="button button--secondary"
            type="button"
            @click="cancelDeletion"
          >
            {{
              pendingDeletion.kind === "checklist"
                ? "Keep item"
                : "Keep question"
            }}
          </button>
          <button
            class="button button--danger"
            type="button"
            @click="confirmDeletion"
          >
            {{
              pendingDeletion.kind === "checklist"
                ? "Delete checklist item"
                : "Delete question"
            }}
          </button>
        </footer>
      </section>
    </div>
    <ScheduleImpactDialog
      :impact="scheduleImpact"
      :can-extend="workspace.canManageProject"
      @close="scheduleImpact = null"
      @apply="applyPendingSchedule"
    />
    <TaskDeleteDialog
      :open="taskDeleteOpen"
      :tasks="task ? [{ id: task.id, title: task.title, type: task.type }] : []"
      :blockers="displayedTaskDeleteBlockers"
      :deleting="taskDeleting"
      :error="taskDeleteError"
      :has-unsaved-changes="dirty"
      @cancel="taskDeleteOpen = false"
      @confirm="confirmTaskDeletion"
    />
    <ArtifactDeleteDialog
      :open="Boolean(fileToDelete || commentToDelete)"
      :kind="fileToDelete ? 'file' : 'comment'"
      :name="
        fileToDelete?.name ||
        (commentToDelete ? `Comment by ${commentToDelete.author}` : '')
      "
      :deleting="artifactDeleting"
      :error="artifactDeleteError"
      @cancel="cancelArtifactDeletion"
      @confirm="confirmArtifactDeletion"
    />
  </Teleport>
</template>

<style scoped>
.task-layer {
  position: fixed;
  inset: 0;
  z-index: 120;
}
.delete-layer {
  position: fixed;
  inset: 0;
  z-index: 140;
  display: grid;
  place-items: center;
  padding: 20px;
}
.delete-scrim {
  position: absolute;
  inset: 0;
  width: 100%;
  background: rgba(8, 27, 46, 0.58);
}
.delete-dialog {
  position: relative;
  width: min(460px, 100%);
  overflow: hidden;
  border-radius: 10px;
  background: var(--sheet);
  box-shadow: var(--shadow-panel);
}
.delete-dialog > header {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 12px;
  padding: 22px;
}
.delete-dialog__icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: var(--danger);
  background: var(--danger-soft);
}
.delete-dialog h2 {
  color: var(--ink-900);
}
.delete-dialog header p {
  margin-top: 7px;
  color: var(--text-soft);
  overflow-wrap: anywhere;
}
.delete-dialog header p strong {
  color: var(--text);
}
.delete-dialog > footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 13px 22px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.delete-dialog .button--danger:hover {
  background: color-mix(in srgb, var(--danger) 88%, var(--ink-950));
}
.task-scrim {
  position: absolute;
  inset: 0;
  width: 100%;
  background: rgba(8, 27, 46, 0.28);
}
.task-panel {
  position: absolute;
  inset: 0 0 0 auto;
  width: min(670px, 100vw);
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  background: white;
  box-shadow: var(--shadow-panel);
}
.task-panel__header {
  position: relative;
  min-height: 78px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 20px;
  padding: 14px 62px 14px 22px;
  border-bottom: 1px solid var(--rule);
}
.task-kind {
  flex: 0 0 auto;
  padding: 3px 7px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-pill);
  color: var(--text-soft);
  background: var(--sheet-subtle);
  font-size: 11px;
  font-weight: 650;
}
.task-heading {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
}
.task-heading h2 {
  min-width: 0;
  font-size: 21px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-meter {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--brand-strong);
}
.task-meter--empty {
  color: var(--rule-strong);
}
.task-meter--complete {
  color: var(--success);
}
.status-context {
  min-height: 30px;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
  background: var(--sheet-strong);
}
.status-context strong {
  color: var(--ink-800);
  font-size: 12px;
  white-space: nowrap;
}
.status-context--complete,
.status-context--not-applicable {
  border-color: color-mix(in srgb, var(--success) 32%, var(--rule));
  background: var(--success-soft);
}
.status-context--blocked {
  border-color: color-mix(in srgb, var(--danger) 32%, var(--rule));
  background: var(--danger-soft);
}
.progress-summary {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--ink-800);
}
.progress-summary__track {
  width: 54px;
  height: 5px;
  overflow: hidden;
  border-radius: var(--radius-pill);
  background: var(--sheet-strong);
}
.progress-summary__track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: currentColor;
}
.task-meter--empty .progress-summary__track i {
  background: var(--rule-strong);
}
.task-meter--complete .progress-summary__track i {
  background: var(--success);
}
.progress-summary strong {
  min-width: 34px;
  font-size: 12px;
  text-align: right;
}
.task-panel__actions {
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  gap: 2px;
}
.task-tabs {
  min-height: 43px;
  display: flex;
  overflow-x: auto;
  padding: 0 13px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.task-tabs button {
  position: relative;
  min-width: fit-content;
  padding: 0 10px;
  background: transparent;
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}
.task-tabs button.active {
  color: var(--text);
}
.task-tabs button.active::after {
  content: "";
  position: absolute;
  inset: auto 9px -1px;
  height: 2px;
  background: var(--brand);
}
.task-panel__body {
  min-height: 0;
  overflow: auto;
}
.draft-notice {
  display: grid;
  gap: 10px;
  margin: 16px 24px 0;
  padding: 12px;
  border-radius: var(--radius-md);
  color: var(--danger);
  background: var(--danger-soft);
}
.draft-notice > div:first-child {
  display: flex;
  align-items: flex-start;
  gap: 9px;
}
.draft-notice > div:first-child svg {
  flex: 0 0 auto;
  margin-top: 1px;
}
.draft-notice--conflict {
  color: #66450d;
  background: var(--warning-soft);
}
.completion-dependencies {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 9px;
  margin: 16px 24px 0;
  padding: 12px;
  border: 1px solid color-mix(in srgb, var(--warning) 26%, var(--rule));
  border-radius: var(--radius-md);
  color: #6f490b;
  background: var(--warning-soft);
}
.completion-dependencies:focus-visible {
  box-shadow: 0 0 0 3px rgba(75, 127, 55, 0.3);
}
.completion-dependencies--attempted {
  border-color: color-mix(in srgb, var(--danger) 28%, var(--rule));
  color: var(--danger);
  background: var(--danger-soft);
}
.completion-dependencies > svg {
  margin-top: 1px;
}
.completion-dependencies > div {
  min-width: 0;
}
.completion-dependencies p {
  margin-top: 2px;
  color: color-mix(in srgb, currentColor 82%, var(--text));
  font-size: 12px;
}
.completion-dependencies__list {
  display: grid;
  gap: 5px;
  margin-top: 9px;
}
.completion-dependencies__list button {
  width: 100%;
  min-height: 43px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 9px;
  border: 1px solid color-mix(in srgb, currentColor 18%, var(--rule));
  border-radius: var(--radius-sm);
  background: var(--sheet);
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.completion-dependencies__list button:hover {
  border-color: color-mix(in srgb, currentColor 35%, var(--rule));
  background: var(--sheet-subtle);
}
.completion-dependencies__list button > span:first-child {
  min-width: 0;
  display: grid;
}
.completion-dependencies__list button strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.completion-dependencies__list button small {
  color: var(--text-soft);
}
.draft-conflict {
  display: grid;
  gap: 9px;
  padding: 11px;
  border: 1px solid color-mix(in srgb, var(--warning) 28%, transparent);
  border-radius: var(--radius-sm);
  background: var(--sheet);
  color: var(--text);
}
.draft-conflict dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
}
.draft-conflict dl > div {
  min-width: 0;
}
.draft-conflict dt {
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 650;
}
.draft-conflict dd {
  margin: 3px 0 0;
  overflow-wrap: anywhere;
}
.draft-conflict > div:last-child {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.task-section {
  display: grid;
  gap: 20px;
  padding: 24px 24px 46px;
}
.overview-group {
  display: grid;
  gap: 14px;
  padding-block: 18px;
  border-block: 1px solid var(--rule);
}
.overview-group__heading {
  display: grid;
  gap: 3px;
}
.overview-group__heading h3 {
  color: var(--ink-800);
  font-size: 15px;
}
.optional-description {
  min-width: 0;
}
.optional-description__add {
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border: 1px dashed var(--rule-strong);
  border-radius: var(--radius-sm);
  background: var(--sheet-subtle);
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.optional-description__add:hover {
  border-color: var(--brand-strong);
  background: var(--brand-soft);
}
.optional-description__add > svg {
  flex: 0 0 auto;
  color: var(--brand-strong);
}
.optional-description__add > span {
  min-width: 0;
  display: grid;
  gap: 1px;
}
.optional-description__add small,
.field-label small {
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 400;
}
.blocker-form {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 10px;
  padding: 13px;
  border-radius: 7px;
  color: var(--danger);
  background: var(--danger-soft);
}
.blocker-form > .field {
  min-width: 0;
  grid-column: 2;
}
.checkbox-row {
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  cursor: pointer;
}
.checkbox-row input {
  flex: 0 0 auto;
}
.internal-only-toggle {
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--internal-soft);
}
.internal-only-toggle input {
  width: 17px;
  height: 17px;
  margin-top: 1px;
  accent-color: var(--internal);
}
.internal-only-toggle span {
  display: grid;
  gap: 1px;
}
.internal-only-toggle small {
  color: var(--text-soft);
}
.work-item-type-field {
  padding-bottom: 14px;
  border-bottom: 1px solid var(--rule);
}
.conversion-notice {
  margin: 0;
  padding: 10px 11px;
  border: 1px solid var(--warning);
  border-radius: var(--radius-sm);
  background: var(--warning-soft);
  color: #6f4308;
  font-size: 12px;
}
.input-with-unit {
  position: relative;
}
.input-with-unit .input {
  padding-right: 98px;
}
.input-with-unit span {
  position: absolute;
  right: 10px;
  top: 10px;
  color: var(--text-soft);
  font-size: 12px;
}
.schedule-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.calculated-finish {
  grid-column: 1 / -1;
  min-height: 58px;
  display: grid;
  align-content: center;
  gap: 2px;
  padding: 9px 11px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--sheet-strong);
}
.calculated-finish span,
.derived-schedule span {
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 650;
}
.calculated-finish strong,
.derived-schedule strong {
  color: var(--ink-800);
  font-variant-numeric: tabular-nums;
}
.schedule-scope {
  grid-column: 1 / -1;
  margin-top: -7px;
  color: var(--text-soft);
  font-size: 12px;
}
.derived-schedule {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
  background: var(--rule);
}
.derived-schedule > div {
  min-width: 0;
  display: grid;
  gap: 3px;
  padding: 12px;
  background: var(--sheet-strong);
}
.readonly-field {
  min-height: 39px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  color: var(--text-soft);
  background: var(--sheet-strong);
}
.progress-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}
.progress-label-row label {
  color: #3e4f5e;
  font-size: 12px;
  font-weight: 650;
}
.progress-value {
  color: var(--ink-800);
  font-size: 14px;
  font-weight: 700;
}
.progress-slider {
  width: 100%;
  min-height: 32px;
  margin: 0;
  accent-color: var(--brand-strong);
  cursor: pointer;
}
.progress-slider:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}
.progress-slider:focus-visible {
  outline: 3px solid rgba(75, 127, 55, 0.24);
  outline-offset: 2px;
}
.dependency-list {
  display: grid;
  gap: 7px;
}
.dependency-group {
  display: grid;
  gap: 7px;
}
.dependency-list > div:first-child,
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.item-import {
  display: grid;
  justify-items: start;
  gap: 8px;
}
.item-import__form {
  width: 100%;
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-sm);
  background: var(--sheet-subtle);
}
.item-import__form label {
  font-size: 12px;
  font-weight: 700;
}
.item-import__form .textarea {
  min-height: 112px;
  resize: vertical;
}
.item-import__form > div {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.dependency-picker {
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--rule-strong);
  border-radius: 8px;
  background: var(--sheet-subtle);
}
.dependency-picker label {
  font-size: 12px;
  font-weight: 700;
}
.dependency-picker > div {
  max-height: 210px;
  overflow: auto;
  display: grid;
  gap: 3px;
}
.dependency-picker button {
  display: grid;
  gap: 2px;
  padding: 9px 10px;
  border-radius: 6px;
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.dependency-picker button:hover,
.dependency-picker button:focus-visible {
  background: white;
}
.blocking-list {
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.blocking-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 0;
  border-bottom: 1px solid var(--rule);
}
.form-error {
  color: var(--danger);
  font-size: 12px;
  font-weight: 650;
}
.dependency {
  min-height: 52px;
  display: grid;
  grid-template-columns: 25px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border: 1px solid var(--rule);
  border-radius: 6px;
}
.dependency__task {
  min-width: 0;
  display: grid;
  padding: 4px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.dependency__task:hover {
  background: var(--sheet-strong);
}
.dependency small {
  color: var(--text-soft);
}
.empty-inline {
  padding: 13px;
  border: 1px dashed var(--rule-strong);
  border-radius: 6px;
  color: var(--text-soft);
}
.checklist {
  display: grid;
  border-block: 1px solid var(--rule);
}
.checklist-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  border-bottom: 1px solid var(--rule);
}
.checklist-row:last-child {
  border-bottom: 0;
}
.checklist label {
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.checklist .done {
  color: var(--text-soft);
  text-decoration: line-through;
}
.reopen-reason {
  padding: 14px;
  border: 1px solid var(--warning);
  border-radius: var(--radius-md);
  background: var(--warning-soft);
}
.questions-heading {
  margin-top: 10px;
}
.questions {
  display: grid;
  gap: 16px;
}
.question-options {
  display: grid;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-sm);
}
.question-options label {
  min-height: 40px;
  display: flex;
  align-items: center;
  gap: 9px;
}
.question-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.question-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.optional-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 600;
}
.inline-builder {
  display: flex;
  align-items: center;
  gap: 7px;
}
.inline-builder .input {
  width: min(230px, 32vw);
}
.inline-builder--question .select {
  width: 130px;
}
.questions label span {
  margin-left: 5px;
  color: var(--danger);
  font-size: 10px;
}
.empty-block {
  min-height: 150px;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 7px;
  padding: 22px;
  border: 1px dashed var(--rule-strong);
  border-radius: 7px;
  color: var(--text-soft);
  text-align: center;
}
.empty-block strong {
  color: var(--text);
}
.file-row {
  width: 100%;
  min-height: 64px;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: var(--sheet);
  color: var(--text);
  text-align: left;
}
.file-icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  color: var(--shared);
  background: var(--shared-soft);
}
.file-row > span:nth-child(2) {
  display: grid;
}
.file-row small {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--text-soft);
}
.file-row__state {
  color: var(--warning);
  font-size: 11px;
}
.file-row__action,
.comment-delete-button {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-soft);
  cursor: pointer;
}
.file-row__action:hover,
.file-row__action:focus-visible {
  background: var(--shared-soft);
  color: var(--shared);
}
.file-row__action--delete:hover,
.file-row__action--delete:focus-visible,
.comment-delete-button:hover,
.comment-delete-button:focus-visible {
  background: var(--danger-soft);
  color: var(--danger);
}
.file-row__action:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}
.thread {
  display: grid;
  gap: 18px;
}
.comment {
  min-width: 0;
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 10px;
}
.comment > div {
  min-width: 0;
}
.comment > div > header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.comment time {
  color: var(--text-faint);
  font-size: 11px;
}
.comment-delete-button {
  width: 28px;
  height: 28px;
  margin-inline-start: auto;
}
.comment-deleted {
  color: var(--text-soft);
  font-style: italic;
}
.comment p {
  margin-top: 5px;
  white-space: pre-wrap;
}
.comment > div > button {
  margin-top: 5px;
  padding: 0;
  background: transparent;
  color: var(--shared);
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}
.comment > div > button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}
.comment--reply {
  margin-top: 14px;
  padding-left: 10px;
  border-left: 1px solid var(--rule-strong);
}
.comment-composer {
  position: sticky;
  min-width: 0;
  bottom: -46px;
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--rule-strong);
  border-radius: 7px;
  background: white;
  box-shadow: 0 -8px 22px rgba(8, 27, 46, 0.05);
}
.comment-composer__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.internal-comment-option {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-right: auto;
  color: var(--text-soft);
  font-size: 12px;
}
.comment-error {
  margin: 0;
}
.comment-composer .select {
  width: 105px;
}
.task-panel__footer {
  min-height: 66px;
  display: grid;
  grid-template-areas: "cancel state save";
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 13px 20px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.footer-cancel {
  grid-area: cancel;
}
.task-panel__footer > span {
  grid-area: state;
}
.save-split {
  grid-area: save;
  position: relative;
  display: inline-flex;
}
.footer-save {
  grid-area: save;
}
.footer-actions {
  grid-area: save;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.footer-actions .footer-save {
  grid-area: auto;
}
.save-split__main {
  border-radius: var(--radius-sm) 0 0 var(--radius-sm);
}
.save-split__toggle {
  width: 36px;
  padding-inline: 0;
  border-left-color: rgba(255, 255, 255, 0.35);
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}
.save-status-menu {
  position: absolute;
  z-index: 4;
  right: 0;
  bottom: calc(100% + 8px);
  width: 224px;
  display: grid;
  gap: 2px;
  padding: 6px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-md);
  background: var(--sheet);
  box-shadow: var(--shadow-menu);
}
.save-status-menu p {
  padding: 5px 8px 3px;
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.save-status-menu button {
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 9px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.save-status-menu button[aria-current="true"] {
  background: var(--sheet-strong);
  font-weight: 650;
}
.save-status-menu .save-status-menu__delete {
  margin-top: 4px;
  border-top: 1px solid var(--rule);
  border-radius: 0 0 var(--radius-sm) var(--radius-sm);
  color: var(--danger);
}
.save-status-menu__label {
  display: flex;
  align-items: center;
  gap: 9px;
}
.status-swatch {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border: 1px solid color-mix(in srgb, currentColor 18%, transparent);
  border-radius: 2px;
  background: var(--rule-strong);
}
.status-swatch--in-progress {
  background: var(--shared);
}
.status-swatch--blocked {
  background: var(--danger);
}
.status-swatch--in-review {
  background: var(--warning);
}
.status-swatch--complete {
  background: var(--success);
}
.status-swatch--not-applicable {
  background: var(--text-faint);
}
.status-swatch--cancelled {
  background: var(--ink-800);
}
.save-status-menu button:hover,
.save-status-menu button:focus-visible {
  background: var(--brand-soft);
}
.save-status-menu button:disabled {
  color: var(--text-faint);
  cursor: default;
  background: var(--sheet-subtle);
}
.conflict-state {
  color: #80500a;
  font-weight: 650;
}
.task-panel-enter-active,
.task-panel-leave-active {
  transition: opacity 180ms var(--ease);
}
.task-panel-enter-active .task-panel,
.task-panel-leave-active .task-panel {
  transition: transform 180ms var(--ease);
}
.task-panel-enter-from,
.task-panel-leave-to {
  opacity: 0;
}
.task-panel-enter-from .task-panel,
.task-panel-leave-to .task-panel {
  transform: translateX(24px);
}
@media (max-width: 700px) {
  .task-panel {
    width: 100%;
  }
  .task-panel__header {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    padding: 12px 52px 12px 16px;
  }
  .task-heading h2 {
    font-size: 18px;
  }
  .task-section {
    padding: 20px 16px 42px;
  }
  .draft-notice {
    margin: 12px 16px 0;
  }
  .completion-dependencies {
    margin: 12px 16px 0;
  }
  .completion-dependencies__list button strong {
    overflow: visible;
    white-space: normal;
  }
  .draft-conflict dl {
    grid-template-columns: 1fr;
  }
  .task-panel__footer {
    padding: 10px 12px;
  }
  .task-panel__footer > span {
    font-size: 11px;
  }
  .task-panel__footer .button {
    padding-inline: 9px;
  }
  .save-split__toggle {
    width: 38px;
    padding-inline: 0;
  }
  .comment > div > header {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .section-heading {
    align-items: stretch;
    flex-direction: column;
  }
  .inline-builder,
  .inline-builder--question {
    display: grid;
    grid-template-columns: 1fr;
  }
  .inline-builder .input,
  .inline-builder--question .select {
    width: 100%;
  }
}
@media (max-width: 520px) {
  .task-panel__header {
    grid-template-columns: 1fr;
    align-content: center;
    gap: 8px;
  }
  .task-heading {
    padding-right: 4px;
  }
  .task-meter {
    justify-content: flex-start;
  }
  .task-panel__footer {
    grid-template-areas:
      "state state"
      "cancel save";
    grid-template-columns: auto minmax(0, 1fr);
  }
  .task-panel__footer > span {
    min-height: 16px;
  }
  .save-split,
  .footer-save,
  .footer-actions {
    justify-self: end;
  }
  .footer-actions {
    flex-wrap: wrap-reverse;
  }
  .status-context {
    min-height: 27px;
  }
  .progress-summary__track {
    width: 46px;
  }
  .comment-composer__actions {
    grid-template-columns: minmax(0, 1fr);
    display: grid;
  }
  .internal-comment-option {
    justify-self: start;
  }
  .comment-composer__actions .button {
    width: 100%;
    min-width: 0;
    padding-inline: 8px;
  }
  .schedule-fields,
  .derived-schedule {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 430px) {
  .delete-layer {
    padding: 12px;
  }
  .delete-dialog > header {
    padding: 18px;
  }
  .delete-dialog > footer {
    grid-template-columns: 1fr;
    display: grid;
    padding: 12px 18px 18px;
  }
  .schedule-fields,
  .derived-schedule {
    grid-template-columns: 1fr;
  }
  .derived-schedule {
    gap: 1px;
  }
}
</style>
